import type { FC } from 'react';
import React, { useState } from 'react';
import { Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useNativeState } from '@shared/core/native';
import { isInIframe } from '@shared/utils/isInIframe';
import { pxToRem } from '@withjoy/joykit/theme';
import usePersistedState from '@shared/hooks/usePersistedState';
import { useWebsiteBanner, WEBSITE_BANNER_PRIORITY } from '@shared/components/WebsiteBanner';
import { ReactComponent as JoyIconMobile } from './icon-joy-mobile.svg';
import { useSmartAppBannerTelemetry } from './SmartAppBanner.telemetry';

const isIframed = isInIframe();

const appLink = 'withjoy://';
const appStoreLink = 'https://apps.apple.com/us/app/joy-wedding-app-website/id994411720';
const googlePlayLink = 'https://play.google.com/store/apps/details?id=com.withjoy.joy';

const SMART_APP_BANNER_VISIBILITY_KEY = 'smartAppBannerVisibility';

// Anyone who already dismissed the banner has 'hidden' stored under this key by the banner's
// former state provider — changing the key or the serializer resurfaces it for all of them.
const visibilityOptions = {
  serialize: (visible: boolean) => (visible ? 'visible' : 'hidden'),
  deserialize: (raw: string): boolean | undefined => (raw === 'hidden' ? false : true),
} as const;

const ICON_SIZE = 40;
// The source SVG's corner ratio at the rendered size; a ring on a different radius shows pinches.
const ICON_BORDER_RADIUS = pxToRem((ICON_SIZE * 10.6667) / 56);

// Ringed because the tile's gradient starts at `#4951EF` — exactly `newUIBrandPrimary` — so it
// would otherwise dissolve into the bar. `box-shadow` keeps the rounded corners and costs no layout.
const SmartAppBannerIcon: FC = () => (
  <Flex
    borderRadius={ICON_BORDER_RADIUS}
    boxShadow="0 0 0 2px white"
  >
    <JoyIconMobile
      width={ICON_SIZE}
      height={ICON_SIZE}
    />
  </Flex>
);

type UseSmartAppBannerArgs = Readonly<{
  /** Passed in, not read here: the controller already has it, and admin-only is the surprising part. */
  isAdmin: boolean;
  telemetryCategory: string;
}>;

/**
 * Registers the "continue in the app" promo into the website banner registry. It sits last in
 * `WEBSITE_BANNER_PRIORITY`, so any actionable banner displaces it until that state is resolved.
 */
export const useSmartAppBanner = ({ isAdmin, telemetryCategory }: UseSmartAppBannerArgs) => {
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobileScreen();
  const { isNative } = useNativeState();

  const telemetry = useSmartAppBannerTelemetry();
  const { tq } = useTranslation('joykit');

  const [isBannerVisible, setIsBannerVisible] = usePersistedState(SMART_APP_BANNER_VISIBILITY_KEY, true, visibilityOptions);

  const handleClose = useEventCallback(() => {
    telemetry.stayOnWebClicked(telemetryCategory);
    setIsBannerVisible(false);
  });

  const handleNavigateToApp = useEventCallback(() => {
    setIsLoading(true);
    const isAndroid = /android/i.test(navigator.userAgent);
    telemetry.weHaveAnAppClicked(isAndroid ? 'GooglePlay' : 'AppStore', telemetryCategory);
    setIsBannerVisible(false);
    const storeLink = isAndroid ? googlePlayLink : appStoreLink;
    window.location.href = appLink;

    setTimeout(function () {
      // if the app is opened, the document won't be focused
      // so if app is not installed, the document will be focused
      if (document.hasFocus()) {
        window.location.href = storeLink;
      }
      setIsLoading(false);
    }, 2000);
  });

  useWebsiteBanner({
    id: 'smart-app',
    priority: WEBSITE_BANNER_PRIORITY.smartApp,
    // `isLoading` holds the bar through the 2s app-open attempt: dismissal persists on click, so
    // without it the bar vanishes instantly and the button's spinner is never seen.
    isVisible: isAdmin && (isBannerVisible || isLoading) && isMobile && !isNative && !isIframed,
    message: {
      default: tq`Continue in the App`,
    },
    icon: <SmartAppBannerIcon />,
    actionButton: {
      label: {
        default: tq`View`,
      },
      onClick: handleNavigateToApp,
      loading: isLoading,
    },
    onClose: handleClose,
    closeButtonPlacement: 'left',
  });
};
